import dayjs from "dayjs";
import { jsPDF } from "jspdf";

import { getQuotationById } from "~/services/quotationService";
import type { QuotationWithDetails } from "~/types/quotation";

const LOGO_URL =
  "https://mdrvhekhimhcwydrpueo.supabase.co/storage/v1/object/public/uploads/assets/aliace-logo.jpg";

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat("es-CL").format(Math.round(value));
};

const formatCurrency = (value: number): string => {
  return `$${formatNumber(value)}`;
};

// Load image and convert to base64
const loadImage = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = reject;
    img.src = url;
  });
};

export async function generateQuotationPdf(
  quotationParam: QuotationWithDetails
): Promise<void> {
  // If items are not loaded, fetch the full quotation with items
  let quotation = quotationParam;
  if (!quotation.items || quotation.items.length === 0) {
    try {
      const fullQuotation = await getQuotationById(quotation.id);
      if (fullQuotation) {
        quotation = fullQuotation;
      }
    } catch (e) {
      console.error("Could not fetch quotation items:", e);
    }
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;

  // Try to load logo
  let logoBase64: string | null = null;
  try {
    logoBase64 = await loadImage(LOGO_URL);
  } catch (e) {
    console.warn("Could not load logo:", e);
  }

  // ==================== HEADER SECTION ====================
  let y = 12;

  // Logo on the left
  if (logoBase64) {
    doc.addImage(logoBase64, "JPEG", margin, y, 40, 20);
  }

  // Company info in center
  const centerX = pageWidth / 2;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("ALIMENTOS Y ACEITES SPA", centerX, y + 4, { align: "center" });

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text(
    "COMERCIALIZACION Y DISTRIBUCION DE PRODUCTOS ALIMENTICIOS",
    centerX,
    y + 9,
    { align: "center" }
  );
  doc.text("CARRETERA GENERAL SAN MARTIN N° 9340, BOD. 6", centerX, y + 13, {
    align: "center",
  });
  doc.text("QUILICURA, SANTIAGO", centerX, y + 17, { align: "center" });
  doc.text("TELEFONOS: (+56 2) 2681 6656", centerX, y + 21, {
    align: "center",
  });

  // Quotation number box on the right
  const boxX = pageWidth - margin - 45;
  const boxY = y;
  const boxWidth = 45;
  const boxHeight = 22;

  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(boxX, boxY, boxWidth, boxHeight);

  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("COTIZACIÓN", boxX + boxWidth / 2, boxY + 7, {
    align: "center",
  });

  doc.setFontSize(12);
  doc.text(
    `N° ${String(quotation.quotation_number).padStart(4, "0")}`,
    boxX + boxWidth / 2,
    boxY + 15,
    {
      align: "center",
    }
  );

  y += 30;

  // ==================== CLIENT & QUOTATION INFO ====================
  const infoBoxY = y;
  const infoBoxHeight = 40;
  doc.setLineWidth(0.3);
  doc.rect(margin, infoBoxY, pageWidth - 2 * margin, infoBoxHeight);

  // Vertical divider
  const dividerX = pageWidth / 2 + 15;
  doc.line(dividerX, infoBoxY, dividerX, infoBoxY + infoBoxHeight);

  // Left side - Client info
  y += 6;
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.text("SEÑOR(ES):", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(
    quotation.client?.name || quotation.client?.company || "-",
    margin + 25,
    y
  );

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("R.U.T.:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(quotation.client?.tax_id || "-", margin + 25, y);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("DIRECCIÓN:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  const address = quotation.delivery_street
    ? `${quotation.delivery_street} ${quotation.delivery_number || ""}, ${quotation.delivery_comuna || ""}`
    : "-";
  doc.text(address.substring(0, 50), margin + 25, y);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("COMUNA/CIUDAD:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(
    `${quotation.delivery_comuna || ""} ${quotation.delivery_city || ""}`.trim() ||
      "-",
    margin + 40,
    y
  );

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("REGIÓN:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(quotation.delivery_state || "-", margin + 25, y);

  // Right side - Quotation details
  let rightY = infoBoxY + 6;
  const rightX = dividerX + 3;

  doc.setFont("helvetica", "bold");
  doc.text("FECHA:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  doc.text(dayjs(quotation.created_at).format("DD/MM/YYYY"), rightX + 35, rightY);

  rightY += 5;
  doc.setFont("helvetica", "bold");
  doc.text("VÁLIDO HASTA:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  doc.text(dayjs(quotation.valid_until).format("DD/MM/YYYY"), rightX + 35, rightY);

  rightY += 5;
  doc.setFont("helvetica", "bold");
  doc.text("CONDICIÓN PAGO:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  doc.text(
    quotation.sales_condition?.name || `${quotation.payment_terms} días`,
    rightX + 35,
    rightY
  );

  rightY += 5;
  doc.setFont("helvetica", "bold");
  doc.text("VENDEDOR:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  const sellerName = quotation.created_by_profile
    ? `${quotation.created_by_profile.name} ${quotation.created_by_profile.lastname}`
    : "-";
  doc.text(sellerName, rightX + 35, rightY);

  // ==================== COMMENTS ROW ====================
  y = infoBoxY + infoBoxHeight + 2;
  if (quotation.comments) {
    doc.setLineWidth(0.3);
    doc.rect(margin, y, pageWidth - 2 * margin, 8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("OBSERVACIONES:", margin + 3, y + 5);
    doc.setFont("helvetica", "normal");
    const obsText = quotation.comments || "-";
    const truncatedObs =
      obsText.length > 80 ? obsText.substring(0, 77) + "..." : obsText;
    doc.text(truncatedObs, margin + 35, y + 5);
    y += 10;
  }

  // ==================== ITEMS TABLE ====================
  y += 2;
  const tableY = y;

  // Table header
  const colWidths = [18, 18, 74, 22, 16, 22];
  const colHeaders = [
    "CÓDIGO",
    "CANT.",
    "DESCRIPCIÓN",
    "P. UNIT",
    "DCTO",
    "TOTAL",
  ];
  const colX: number[] = [margin];

  // Calculate column positions
  for (let i = 1; i < colWidths.length; i++) {
    colX.push(colX[i - 1] + colWidths[i - 1]);
  }

  // Draw header row
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, tableY, pageWidth - 2 * margin, 6, "F");
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(margin, tableY, pageWidth - 2 * margin, 6);

  // Draw header text
  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  colHeaders.forEach((header, i) => {
    doc.text(header, colX[i] + 1, tableY + 4);
  });

  // Draw vertical lines for header
  colX.forEach((x) => {
    doc.line(x, tableY, x, tableY + 6);
  });
  doc.line(pageWidth - margin, tableY, pageWidth - margin, tableY + 6);

  // Items rows
  y = tableY + 6;
  const rowHeight = 7;
  const items = quotation.items || [];
  const maxRows = Math.max(items.length, 8);
  const itemsTableHeight = maxRows * rowHeight;

  // Draw table body outline
  doc.rect(margin, y, pageWidth - 2 * margin, itemsTableHeight);

  // Draw vertical column lines for body
  colX.forEach((x) => {
    doc.line(x, y, x, y + itemsTableHeight);
  });
  doc.line(pageWidth - margin, y, pageWidth - margin, y + itemsTableHeight);

  // Fill in items
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);

  for (let index = 0; index < maxRows; index++) {
    const itemY = y + 5 + index * rowHeight;

    // Draw horizontal row line (except for first row)
    if (index > 0) {
      doc.setDrawColor(200, 200, 200);
      doc.line(
        margin,
        y + index * rowHeight,
        pageWidth - margin,
        y + index * rowHeight
      );
      doc.setDrawColor(0);
    }

    if (index < items.length) {
      const item = items[index];
      const lineTotal =
        item.quantity * item.unit_price * (1 - (item.discount_percent || 0) / 100);

      // Code
      const productCode =
        item.product?.internal_id || item.product?.sku || String(index + 1);
      doc.text(productCode.substring(0, 8), colX[0] + 1, itemY);

      // Quantity
      doc.text(formatNumber(item.quantity), colX[1] + 1, itemY);

      // Description
      const desc = item.product?.short_name || item.product?.long_name || "-";
      const truncatedDesc = desc.length > 45 ? desc.substring(0, 42) + "..." : desc;
      doc.text(truncatedDesc, colX[2] + 1, itemY);

      // Unit price
      doc.text(formatCurrency(item.unit_price), colX[3] + 1, itemY);

      // Discount
      doc.text(
        item.discount_percent ? `${item.discount_percent}%` : "-",
        colX[4] + 1,
        itemY
      );

      // Total
      doc.text(formatCurrency(lineTotal), colX[5] + 1, itemY);
    }
  }

  // ==================== TOTALS SECTION ====================
  y = tableY + 6 + itemsTableHeight + 5;

  const totalsWidth = 60;
  const totalsX = pageWidth - margin - totalsWidth;
  const totalsHeight = 30;

  doc.setLineWidth(0.3);
  doc.rect(totalsX, y, totalsWidth, totalsHeight);

  doc.setFontSize(7);
  let totY = y + 6;

  // Subtotal (Net)
  doc.setFont("helvetica", "normal");
  doc.text("SUBTOTAL NETO:", totalsX + 2, totY);
  doc.text(
    formatCurrency(quotation.total_amount),
    totalsX + totalsWidth - 3,
    totY,
    { align: "right" }
  );

  // IVA
  totY += 6;
  doc.text("IVA 19%:", totalsX + 2, totY);
  const ivaAmount = (quotation.price_with_iva || 0) - quotation.total_amount;
  doc.text(formatCurrency(ivaAmount), totalsX + totalsWidth - 3, totY, {
    align: "right",
  });

  // Total
  totY += 6;
  doc.setFont("helvetica", "bold");
  doc.text("TOTAL:", totalsX + 2, totY);
  doc.text(
    formatCurrency(quotation.price_with_iva || 0),
    totalsX + totalsWidth - 3,
    totY,
    { align: "right" }
  );

  // ==================== TERMS SECTION ====================
  const termsY = y;
  const termsWidth = pageWidth - margin * 2 - totalsWidth - 5;

  doc.setLineWidth(0.3);
  doc.rect(margin, termsY, termsWidth, totalsHeight);

  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  doc.text("TÉRMINOS Y CONDICIONES:", margin + 2, termsY + 5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(5);

  const terms = [
    `• Esta cotización es válida hasta el ${dayjs(quotation.valid_until).format("DD/MM/YYYY")}`,
    "• Los precios están expresados en pesos chilenos (CLP)",
    "• Los precios no incluyen flete salvo indicación contraria",
    "• Forma de pago sujeta a evaluación crediticia",
  ];

  let termY = termsY + 10;
  terms.forEach((term) => {
    doc.text(term, margin + 2, termY);
    termY += 4;
  });

  // ==================== BANKING DETAILS SECTION ====================
  const bankingY = y + totalsHeight + 4;
  const bankingHeight = 20;

  doc.setTextColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(margin, bankingY, pageWidth - 2 * margin, bankingHeight);

  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  doc.text("TRANSFERENCIAS Y DEPÓSITOS", margin + 2, bankingY + 5);

  doc.setFontSize(5);
  const bankingLeft = [
    "A NOMBRE DE: ALIMENTOS Y ACEITES SPA",
    "R.U.T.: 76.505.808-2",
    "CORREO: pagos@aliace.cl",
  ];
  const bankingRight = [
    "BANCO SANTANDER — N° CTA 69977499",
    "BANCO CHILE — N° CTA 00-800-57631-04",
  ];

  doc.setFont("helvetica", "normal");
  let bankingLineY = bankingY + 10;
  bankingLeft.forEach((line) => {
    doc.text(line, margin + 2, bankingLineY);
    bankingLineY += 3.5;
  });

  const bankingRightX = margin + (pageWidth - 2 * margin) / 2;
  bankingLineY = bankingY + 10;
  bankingRight.forEach((line) => {
    doc.text(line, bankingRightX, bankingLineY);
    bankingLineY += 3.5;
  });

  // ==================== FOOTER ====================
  y = bankingY + bankingHeight + 8;

  // Disclaimer
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(100, 100, 100);
  doc.text(
    "Esta cotización no constituye una orden de compra.",
    pageWidth / 2,
    y,
    { align: "center" }
  );

  // Generation timestamp
  doc.setFontSize(5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(150, 150, 150);
  doc.text(
    `Documento generado el ${dayjs().format("DD/MM/YYYY HH:mm")}`,
    pageWidth / 2,
    y + 5,
    { align: "center" }
  );

  // Open PDF in new tab
  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, "_blank");
}
